import { memo, useState, useEffect } from 'preact/compat';
import { apiFetch } from '../api/client';
import LoadingOverlay from './LoadingOverlay';

interface BuyerMapping {
    id: number;
    oracle_buyer_code: number;
    oracle_buyer_name: string;
    oracle_login: string | null;
    b24_user_id: number | null;
    b24_user_name: string | null;
    b24_manager_id: number | null;
    role: string;
    matched_auto: boolean;
}

interface B24User {
    id: number;
    name: string;
    email: string;
}

interface B24Status {
    connected: boolean;
    domain?: string;
    expired?: boolean;
}

const ROLE_LABELS: Record<string, string> = {
    buyer: 'Байер',
    manager: 'Руководитель',
    chief_buyer: 'Главный байер',
};

const AdminPage = memo(() => {
    const [mapping, setMapping] = useState<BuyerMapping[]>([]);
    const [b24Users, setB24Users] = useState<B24User[]>([]);
    const [b24Status, setB24Status] = useState<B24Status | null>(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editB24UserId, setEditB24UserId] = useState<string>('');
    const [editRole, setEditRole] = useState<string>('buyer');
    const [searchText, setSearchText] = useState('');
    const [filterMatch, setFilterMatch] = useState<'all' | 'matched' | 'unmatched'>('all');

    const loadData = async () => {
        setLoading(true);
        try {
            const [m, s] = await Promise.all([
                apiFetch<BuyerMapping[]>('/buyer-mapping'),
                apiFetch<B24Status>('/b24/status'),
            ]);
            setMapping(m);
            setB24Status(s);
        } catch {}
        setLoading(false);
    };

    const loadB24Users = async () => {
        try {
            setB24Users(await apiFetch<B24User[]>('/b24/users'));
        } catch {}
    };

    useEffect(() => { loadData(); }, []);

    const handleSync = async () => {
        setSyncing(true);
        try {
            await apiFetch('/buyer-mapping/sync', { method: 'POST' });
            await loadData();
        } catch {}
        setSyncing(false);
    };

    const startEdit = (m: BuyerMapping) => {
        setEditingId(m.id);
        setEditB24UserId(m.b24_user_id ? String(m.b24_user_id) : '');
        setEditRole(m.role);
        if (b24Users.length === 0) loadB24Users();
    };

    const saveEdit = async (id: number) => {
        const b24User = b24Users.find(u => u.id === Number(editB24UserId));
        try {
            await apiFetch(`/buyer-mapping/${id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    b24_user_id: Number(editB24UserId) || null,
                    b24_user_name: b24User?.name || null,
                    role: editRole,
                }),
            });
            setEditingId(null);
            await loadData();
        } catch {}
    };

    let displayMapping = [...mapping];
    if (searchText) {
        const q = searchText.toLowerCase();
        displayMapping = displayMapping.filter(m =>
            m.oracle_buyer_name.toLowerCase().includes(q) ||
            (m.b24_user_name || '').toLowerCase().includes(q) ||
            (m.oracle_login || '').toLowerCase().includes(q)
        );
    }
    if (filterMatch === 'matched') displayMapping = displayMapping.filter(m => m.b24_user_id);
    if (filterMatch === 'unmatched') displayMapping = displayMapping.filter(m => !m.b24_user_id);

    const matchedCount = mapping.filter(m => m.b24_user_id).length;

    return (
        <div className="h-full flex flex-col relative">
            <LoadingOverlay visible={loading} />

            <div className="px-6 py-3 border-b border-base-300 bg-base-100 flex items-center gap-3 flex-wrap">
                <h3 className="font-semibold">Сопоставление байеров</h3>

                {b24Status && (
                    <span className={`badge badge-sm ${b24Status.connected ? 'badge-success' : 'badge-error'}`}>
                        {b24Status.connected ? b24Status.domain : 'Б24 не подключен'}
                    </span>
                )}

                <span className="text-sm text-base-content/60">
                    {matchedCount}/{mapping.length} сопоставлено
                </span>

                <div className="flex-1" />

                <input type="text" placeholder="Поиск..." className="input input-bordered input-sm w-40"
                    value={searchText} onInput={e => setSearchText((e.target as HTMLInputElement).value)} />

                <select className="select select-bordered select-sm" value={filterMatch}
                    onChange={e => setFilterMatch((e.target as HTMLSelectElement).value as any)}>
                    <option value="all">Все</option>
                    <option value="matched">Сопоставлены</option>
                    <option value="unmatched">Не сопоставлены</option>
                </select>

                <button className={`btn btn-sm btn-accent ${syncing ? 'loading' : ''}`}
                    disabled={syncing} onClick={handleSync}>
                    {syncing ? 'Синхронизация...' : 'Синхронизировать'}
                </button>
            </div>

            <div className="flex-1 overflow-auto p-4">
                <table className="table table-sm w-full">
                    <thead>
                        <tr className="bg-base-200">
                            <th className="text-xs">Oracle байер</th>
                            <th className="text-xs">Логин</th>
                            <th className="text-xs">Б24 пользователь</th>
                            <th className="text-xs">Роль</th>
                            <th className="text-xs">Руководитель (Б24 ID)</th>
                            <th className="text-xs">Матч</th>
                            <th className="text-xs text-center">Действие</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayMapping.map(m => {
                            const isEditing = editingId === m.id;
                            return (
                                <tr key={m.id} className={`hover ${!m.b24_user_id ? 'bg-error/5' : ''}`}>
                                    <td className="text-sm font-medium">{m.oracle_buyer_name}</td>
                                    <td className="text-sm text-base-content/60">{m.oracle_login || '\u2014'}</td>
                                    <td className="text-sm">
                                        {isEditing ? (
                                            <select className="select select-xs select-bordered w-full"
                                                value={editB24UserId}
                                                onChange={e => setEditB24UserId((e.target as HTMLSelectElement).value)}>
                                                <option value="">-- не выбран --</option>
                                                {b24Users.map(u => (
                                                    <option key={u.id} value={u.id}>{u.name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            m.b24_user_name || <span className="text-error">не сопоставлен</span>
                                        )}
                                    </td>
                                    <td className="text-sm">
                                        {isEditing ? (
                                            <select className="select select-xs select-bordered"
                                                value={editRole}
                                                onChange={e => setEditRole((e.target as HTMLSelectElement).value)}>
                                                <option value="buyer">Байер</option>
                                                <option value="manager">Руководитель</option>
                                                <option value="chief_buyer">Главный байер</option>
                                            </select>
                                        ) : (
                                            <span className={`badge badge-sm ${m.role === 'manager' ? 'badge-accent' : 'badge-ghost'}`}>
                                                {ROLE_LABELS[m.role] || m.role}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-sm text-base-content/60">{m.b24_manager_id || '\u2014'}</td>
                                    <td className="text-sm">
                                        {m.b24_user_id ? (
                                            <span className={`badge badge-xs ${m.matched_auto ? 'badge-info' : 'badge-warning'}`}>
                                                {m.matched_auto ? 'авто' : 'ручной'}
                                            </span>
                                        ) : '\u2014'}
                                    </td>
                                    <td className="text-center">
                                        {isEditing ? (
                                            <div className="flex gap-1 justify-center">
                                                <button className="btn btn-xs btn-success" onClick={() => saveEdit(m.id)}>OK</button>
                                                <button className="btn btn-xs btn-ghost" onClick={() => setEditingId(null)}>X</button>
                                            </div>
                                        ) : (
                                            <button className="btn btn-xs btn-ghost" onClick={() => startEdit(m)}>
                                                Изменить
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
});

export default AdminPage;
